import { Box, Text, Flex } from "@chakra-ui/react";
import { Col, Row } from "antd";
import RowTable from "./components/RowTable";
import TagDiaTransacao from "./components/TagDiaTransacao";

import colors from "core/resources/theme/colors";
import { ConciliacaoBancariaItemBase } from "features/conciliacao-bancaria/typings";
import TagValorConciliacao from "./components/TagValorConciliacao";
import { formatCurrency } from "@brazilian-utils/brazilian-utils";
import { IoMdTrash } from "react-icons/io";
import useExcluirExtrato from "features/conciliacao-bancaria/hooks/useExcluirExtrato";
import useDesconciliarExtrato from "features/conciliacao-bancaria/hooks/useDesconciliarExtrato";
import useConciliarExtrato from "features/conciliacao-bancaria/hooks/useConciliarExtrato";
import { RiFolderAddFill } from "react-icons/ri";
import { MdLibraryAdd } from "react-icons/md";
import { RowData } from "./components/RowTable";
import { useLoading } from "components/GlobalLoading";
import { useState } from "react";
import { OperacaoEnum } from "core/resources/enums/conta";
import ModalTransferencia from "components/ModalTransferencia";
import { showSuccess, showConfirm } from "infra/helpers/alerta";
import ModalFormConta from "components/Features/Financeiro/FormConta/ModalFormConta";
import { checkOrPermission } from "infra/helpers/permission";
import { PermissaoEnum } from "core/resources/enums/permissao";
import { useMemo } from "react";
import ModalContasNaoConciliadas from "./components/ModalContasNaoConciliadas/index";
import { FiltroConciliacao } from "./components/ModalFiltroConciliacao/hooks/useFiltroConciliacao";
import { formatMoney } from "infra/helpers/number";

interface TableConciliacaoProps {
  value: ConciliacaoBancariaItemBase;
  diaTransacao: string;
  mes: string;
  refetch?: () => any;
  totalContasSemExtrato?: number;
  totalLiquidoNaoConciliadoReceitas?: number;
  totalLiquidoNaoConciliadoDespesas?: number;
  filtro?: FiltroConciliacao;
}

export default function TableConciliacao({
  value,
  diaTransacao,
  mes,
  refetch,
  totalContasSemExtrato,
  totalLiquidoNaoConciliadoReceitas,
  totalLiquidoNaoConciliadoDespesas,
  filtro,
}: TableConciliacaoProps) {
  const [idExtratoNovaTransferencia, setIdExtratoNovaTransferencia] = useState<
    string | undefined
  >();
  const [idExtratoNovoLancamento, setIdExtratoNovoLancamento] = useState<
    string | undefined
  >();
  const [isOpenModalContasNaoConciliadas, setIsOpenModalContasNaoConciliadas] =
    useState<boolean>(false);

  const { mutate: desconciliar, isLoading: isLoadingDesconciliar } =
    useDesconciliarExtrato();

  const { mutate: conciliar, isLoading: isLoadingConciliar } =
    useConciliarExtrato();

  const { mutate: excluir, isLoading: isLoadingExcluir } = useExcluirExtrato();

  const actions = [
    {
      title: "Cancelar Conciliação",
      icon: () => <IoMdTrash color={colors.red} />,
      shouldRender: (v: RowData) =>
        !!v.conciliado &&
        checkOrPermission([
          PermissaoEnum.Backoffice.Financeiro.ConciliacaoBancaria.Alterar,
        ]),
      onClick: async (v: RowData) => {
        const { isConfirmed } = await showConfirm(
          "Deseja realmente desconciliar essa transação?"
        );

        if (isConfirmed) {
          desconciliar(v.id, {
            onSuccess: refetch,
          });
        }
      },
    },
    {
      title: "Novo Lançamento",
      icon: () => <RiFolderAddFill />,
      shouldRender: (v: RowData) =>
        checkOrPermission([
          PermissaoEnum.Backoffice.Financeiro.ConciliacaoBancaria.Conciliar,
        ]),
      onClick: (v: RowData) => {
        setIdExtratoNovoLancamento(v.id);
      },
    },
    {
      title: "Nova Transferência",
      icon: () => <MdLibraryAdd />,
      shouldRender: (v: RowData) =>
        checkOrPermission([
          PermissaoEnum.Backoffice.Financeiro.ConciliacaoBancaria.Conciliar,
        ]),
      onClick: (v: RowData) => {
        setIdExtratoNovaTransferencia(v.id);
      },
    },
    {
      title: "Excluir",
      icon: () => <IoMdTrash color={colors.red} />,
      shouldRender: (v: RowData) =>
        !v.conciliado &&
        checkOrPermission([
          PermissaoEnum.Backoffice.Financeiro.ConciliacaoBancaria.Excluir,
        ]),
      onClick: async (v: RowData) => {
        const { isConfirmed } = await showConfirm(
          "Deseja realmente excluir essa transação?"
        );

        if (isConfirmed) {
          excluir(v.id, {
            onSuccess: refetch,
          });
        }
      },
    },
  ];

  useLoading(isLoadingConciliar || isLoadingDesconciliar || isLoadingExcluir);

  const extratoSelecionadoValor = value.dados?.find(
    (extrato) => extrato.id === idExtratoNovoLancamento
  )?.valor;

  const operacaoContaModal =
    extratoSelecionadoValor && extratoSelecionadoValor > 0
      ? OperacaoEnum.Receita
      : OperacaoEnum.Despesa;

  const onCreateSuccess = ({
    extratoId,
    contas,
    transferenciaId,
  }: {
    extratoId: string;
    contas?: string[];
    transferenciaId?: string;
  }) => {
    conciliar(
      {
        extratoId,
        contas,
        transferenciaId,
      },
      {
        onSuccess: () => {
          showSuccess().then(() => {
            refetch?.();
            setIdExtratoNovoLancamento(undefined);
            setIdExtratoNovaTransferencia(undefined);
          });
        },
      }
    );
  };

  const selectedExtrato = idExtratoNovaTransferencia
    ? value.dados.find((i) => i.id === idExtratoNovaTransferencia)
    : idExtratoNovoLancamento
    ? value.dados.find((i) => i.id === idExtratoNovoLancamento)
    : undefined;

  const defaultDataModalConta: any = useMemo(
    () => ({
      dataVencimento: value.data,
      descricao: selectedExtrato?.descricaoExtratoBancario,
      tenantContaId: selectedExtrato?.tenantContaId,
      valor: Math.abs(selectedExtrato?.valor || 0),
      pagarConta: {
        dataMovimentacao: value.data,
      },
    }),
    [
      selectedExtrato?.descricaoExtratoBancario,
      selectedExtrato?.tenantContaId,
      selectedExtrato?.valor,
      value.data,
    ]
  );

  return (
    <Box as={Col} p="16px" pb="8px">
      <TagDiaTransacao diaTransacao={diaTransacao} />

      {totalContasSemExtrato ? (
        <Flex
          bg={colors.red500}
          color="white"
          cursor="pointer"
          p={4}
          mx={"-16px"}
          roundedTop={16}
          role="button"
          onClick={() => setIsOpenModalContasNaoConciliadas(true)}
          _hover={{
            opacity: 0.8,
          }}
        >
          {totalContasSemExtrato === 1
            ? "Existe uma conta"
            : `Existem ${totalContasSemExtrato} contas`}{" "}
          nesse dia que não{" "}
          {totalContasSemExtrato === 1 ? "foi conciliada" : "foram conciliadas"}
          {totalLiquidoNaoConciliadoDespesas ||
          totalLiquidoNaoConciliadoReceitas ? (
            <Text fontWeight="bold" pl={2}>
              (
              {totalLiquidoNaoConciliadoReceitas
                ? `RECEITA: ${formatMoney(totalLiquidoNaoConciliadoReceitas)}`
                : ""}
              {totalLiquidoNaoConciliadoReceitas &&
              totalLiquidoNaoConciliadoDespesas
                ? `, `
                : ""}
              {totalLiquidoNaoConciliadoDespesas
                ? `DESPESA: ${formatMoney(totalLiquidoNaoConciliadoDespesas)}`
                : ""}
              )
            </Text>
          ) : (
            ""
          )}
        </Flex>
      ) : null}

      {value.dados.map((v, index) => (
        <RowTable
          rowData={{
            ...v,
            data: value.data,
            primeiroItem: index === 0 && !totalContasSemExtrato,
            ultimoItem: index === value.dados.length - 1,
            bg: index % 2 === 0 ? colors.white : "#F8F8F8",
          }}
          actions={actions}
          mes={mes}
          refetch={refetch}
          contasBancariasIds={filtro?.contasBancariasIds}
        />
      ))}

      <Box
        as={Row}
        gutter={32}
        py={2}
        roundedBottom={16}
        bg={value.dados.length % 2 === 0 ? colors.white : "#F8F8F8"}
      >
        <Col xs={12}>
          <Flex justify="space-between">
            <Text fontWeight="bold">Saldo</Text>{" "}
            <Text
              color={value.saldo && value.saldo < 0 ? colors.red : undefined}
            >
              {value.saldo ? (
                `R$ ${formatCurrency(value.saldo)}`
              ) : (
                <TagValorConciliacao />
              )}
            </Text>
          </Flex>
        </Col>
      </Box>

      <ModalTransferencia
        visible={!!idExtratoNovaTransferencia}
        onClose={() => setIdExtratoNovaTransferencia(undefined)}
        defaultData={{
          data: value.data,
          valor: Math.abs(selectedExtrato?.valor || 0),
          contaDestinoId:
            selectedExtrato?.valor && selectedExtrato?.valor > 0
              ? selectedExtrato.tenantContaId
              : undefined,
          contaOrigemId:
            selectedExtrato?.valor && selectedExtrato?.valor < 0
              ? selectedExtrato.tenantContaId
              : undefined,
        }}
        onSuccess={(data) => {
          const transferenciaId = data.id;

          onCreateSuccess({
            transferenciaId,
            extratoId: idExtratoNovaTransferencia!,
          });
        }}
      />

      <ModalFormConta
        operacao={operacaoContaModal}
        isOpen={!!idExtratoNovoLancamento}
        onClose={() => setIdExtratoNovoLancamento(undefined)}
        requiredLiquidar
        fixedDataMovimentacao={value.data}
        showRegisterAnother={false}
        defaultData={defaultDataModalConta}
        onSuccessImmediatelyAfterSave={false}
        onSuccess={(data: { data: string }) => {
          const contas = [data.data];

          onCreateSuccess({
            contas,
            extratoId: idExtratoNovoLancamento!,
          });
        }}
      />

      {isOpenModalContasNaoConciliadas ? (
        <ModalContasNaoConciliadas
          visible={isOpenModalContasNaoConciliadas}
          onClose={() => setIsOpenModalContasNaoConciliadas(false)}
          data={`${mes}-${diaTransacao}`}
          filtro={filtro}
          refetchExtratos={refetch}
        />
      ) : null}
    </Box>
  );
}
